import axios from 'axios';
import { ProductMeasurement, ProductFormData } from '../types';

// Baza URL-a za API:
// 1) ako je postavljen REACT_APP_API_BASE_URL – koristi njega (najviši prioritet)
// 2) ako radiš lokalno (localhost / 127.0.0.1) – koristi lokalni backend
// 3) u suprotnom – koristi produkcioni domen
let API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

if (!API_BASE_URL) {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      API_BASE_URL = 'http://localhost:3001/api';
    } else {
      API_BASE_URL = 'https://thwl.eightcode.com/api';
    }
  } else {
    // Fallback kad nema window (npr. server-side build)
    API_BASE_URL = 'https://thwl.eightcode.com/api';
  }
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Omogući cookies za refresh token
});

// Flag za praćenje refresh token procesa
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

// Procesiranje queue-a nakon refresh-a
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

// Dodaj token u header za sve zahteve
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor za automatski refresh token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isRefreshEndpoint = originalRequest.url?.includes('/users/refresh-token');

    // Na bilo koji 401 (osim na sam refresh) pokušaj refresh pa ponovi zahtev
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isRefreshEndpoint
    ) {
      if (isRefreshing) {
        // Ako je već u toku refresh, dodaj u queue
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          const token = localStorage.getItem('token');
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch((err) => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post(`${API_BASE_URL}/users/refresh-token`, {}, {
          withCredentials: true
        });
        
        const { token } = response.data;
        localStorage.setItem('token', token);
        
        // Ažuriraj authorization header
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        originalRequest.headers.Authorization = `Bearer ${token}`;
        
        processQueue(null, token);
        
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        
        // Refresh token je istekao, preusmeri na login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Postavi flag za loading screen
        sessionStorage.setItem('shouldShowLoading', 'true');
        
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const measurementsApi = {
  getAll: async (): Promise<ProductMeasurement[]> => {
    const response = await api.get('/measurements');
    return response.data;
  },
  getById: async (id: string): Promise<ProductMeasurement> => {
    const response = await api.get(`/measurements/${id}`);
    return response.data;
  },
  getFiltered: async (productType?: string, startDate?: string, endDate?: string): Promise<ProductMeasurement[]> => {
    const params = new URLSearchParams();
    if (productType) params.append('productType', productType);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const response = await api.get(`/measurements?${params.toString()}`);
    return response.data;
  },
  create: async (data: ProductFormData): Promise<ProductMeasurement> => {
    const response = await api.post('/measurements', data);
    return response.data;
  },
  update: async (id: string, data: Partial<ProductFormData>): Promise<ProductMeasurement> => {
    const response = await api.put(`/measurements/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/measurements/${id}`);
  },
  getProductTypes: async (): Promise<string[]> => {
    const response = await api.get('/measurements/product-types');
    return response.data;
  },
};

export const productsApi = {
  getAll: async () => {
    const response = await api.get('/products');
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/products', data);
    return response.data;
  },
  update: async (id: string, data: any) => {
    const response = await api.put(`/products/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    await api.delete(`/products/${id}`);
  },
  bulkCreate: async (products: any[]) => {
    const response = await api.post('/products/bulk', { products });
    return response.data;
  },
};

export const lambdaGroupsApi = {
  getAll: async () => {
    const response = await api.get('/lambda-groups');
    return response.data;
  },
  create: async (data: { name: string; description?: string }) => {
    const response = await api.post('/lambda-groups', data);
    return response.data;
  },
  update: async (id: string, data: { name: string; description?: string }) => {
    const response = await api.put(`/lambda-groups/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    await api.delete(`/lambda-groups/${id}`);
  },
};

export const activeSessionsApi = {
  list: async () => {
    const response = await api.get('/active-sessions/list', {
      params: { _t: Date.now() },
    });
    return response.data;
  },
  cleanupDuplicates: async () => {
    const response = await api.post('/active-sessions/cleanup-duplicates', null, {
      params: { _t: Date.now() },
    });
    return response.data;
  },
  takeOver: async (sessionId: string) => {
    const response = await api.post(
      '/active-sessions/take-over',
      { sessionId },
      { params: { _t: Date.now() } }
    );
    return response.data;
  },
};

export const templatesApi = {
  getByProductId: async (productId: string) => {
    const response = await api.get(`/templates/${productId}`);
    return response.data;
  },
  saveTemplate: async (productId: string, data: any) => {
    const response = await api.put(`/templates/${productId}`, data);
    return response.data;
  },
};

export const usersApi = {
  getAll: async () => {
    const response = await api.get('/users');
    return response.data;
  },
  changeUserPassword: async (userId: string, newPassword: string) => {
    const response = await api.post('/users/change-user-password', { userId, newPassword });
    return response.data;
  },
  changePassword: async (oldPassword: string, newPassword: string) => {
    const response = await api.post('/users/change-password', { oldPassword, newPassword });
    return response.data;
  },
  create: async (userData: { username: string; password: string; role: string }) => {
    const response = await api.post('/users/register', userData);
    return response.data;
  },
  update: async (userId: string, userData: { username: string; role: string }) => {
    const response = await api.put(`/users/${userId}`, userData);
    return response.data;
  },
  delete: async (userId: string, options?: { inheritTo?: string; keepOriginal?: boolean }) => {
    const response = await api.delete(`/users/${userId}`, { data: options });
    return response.data;
  },
  impersonate: async (userId: string) => {
    try {
      // console.log('API - impersonate poziv sa userId:', userId);
      const response = await api.post('/users/impersonate', { userId });
      // console.log('API - impersonate response:', response.data);
      return response.data;
    } catch (error: any) {
      // console.error('API Error - Impersonate:', error.response?.data || error.message);
      // console.error('API Error - Full error:', error);
      throw error; // Re-throw da se može uhvatiti u komponenti
    }
  },
  logout: async () => {
    const response = await api.post('/users/logout');
    return response.data;
  },
  login: async (credentials: { username: string; password: string }) => {
    const response = await api.post('/users/login', credentials);
    return response.data;
  },
  // Novi endpoint za refresh token
  refreshToken: async () => {
    const response = await api.post('/users/refresh-token');
    return response.data;
  },
};

export const tableSettingsApi = {
  getSettings: async () => {
    const response = await api.get('/table-settings');
    return response.data;
  },
  saveSettings: async (settings: any, isGlobal: boolean = false) => {
    const response = await api.put('/table-settings', { ...settings, isGlobal });
    return response.data;
  },
  // Odvojene funkcije za različite tipove podešavanja
  saveColumnSettings: async (columns: any[], isGlobal: boolean = false) => {
    const response = await api.put('/table-settings/columns', { columns, isGlobal });
    return response.data;
  },
  getColumnSettings: async () => {
    const response = await api.get('/table-settings/columns');
    return response.data;
  },
};

export const formSettingsApi = {
  getFormSettings: async () => {
    const response = await api.get('/form-settings');
    return response.data;
  },
  getGlobalFormSettings: async () => {
    const response = await api.get('/form-settings/global');
    return response.data;
  },
  saveFormSettings: async (hideAfrDetails: boolean, hideReferentnaVrednost: boolean) => {
    const response = await api.put('/form-settings', { hideAfrDetails, hideReferentnaVrednost });
    return response.data;
  },
};

export const activityLogsApi = {
  // Dohvatanje logova sa paginacijom i filterima
  getLogs: async (params: {
    page?: number;
    limit?: number;
    logId?: string;
    username?: string;
    userRole?: string;
    action?: string;
    resource?: string;
    status?: string;
    sessionId?: string;
    requestId?: string;
    resourceId?: string;
  }) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value.toString());
      }
    });
    const response = await api.get(`/activity-logs?${searchParams.toString()}`);
    return response.data;
  },

  // Statistike logova
  getStats: async () => {
    const response = await api.get('/activity-logs/stats');
    return response.data;
  },

  // Aktivni korisnici
  getActiveUsers: async () => {
    const response = await api.get('/activity-logs/active-users');
    return response.data;
  },

  // Bulk brisanje logova
  bulkDelete: async (criteria: {
    logIds?: string[];
    username?: string;
    action?: string;
    resource?: string;
    startDate?: string;
    endDate?: string;
    olderThan?: number;
    confirm: boolean;
  }) => {
    const response = await api.post('/activity-logs/bulk-delete', criteria);
    return response.data;
  },

  // Bulk brisanje svih logova
  bulkDeleteAll: async () => {
    const response = await api.post('/activity-logs/bulk-delete-all', { confirm: true });
    return response.data;
  },

  // Preview za bulk brisanje
  bulkDeletePreview: async (criteria: {
    username?: string;
    action?: string;
    resource?: string;
    startDate?: string;
    endDate?: string;
    olderThan?: number;
  }) => {
    const params = new URLSearchParams();
    Object.entries(criteria).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    const response = await api.get(`/activity-logs/bulk-delete-preview?${params.toString()}`);
    return response.data;
  },
};

export default api; 